import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { 
  ShoppingBag, 
  Search, 
  CheckCircle2, 
  Clock, 
  Loader2, 
  AlertTriangle,
  RefreshCw,
  Phone,
  User,
  Ticket
} from 'lucide-react';

const RedemptionOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('Pending'); // 'Pending', 'Fulfilled', 'All'
  const [searchQuery, setSearchQuery] = useState('');
  const [fulfillingId, setFulfillingId] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      // Use the local storage or api helper for bearer token
      const response = await api.get(`/api/owner/store/redemptions?status=${statusFilter}`);
      setOrders(response.data);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch redemption orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleFulfill = async (orderId) => {
    if (fulfillingId) return;
    setFulfillingId(orderId);
    setError('');
    setSuccess('');
    try {
      const response = await api.patch(`/api/owner/store/redemptions/${orderId}/fulfill`);
      if (response.status === 200) {
        setSuccess('Order marked as fulfilled successfully!');
        setTimeout(() => setSuccess(''), 4000);
        fetchOrders();
      } else {
        throw new Error('Fulfillment failed');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to fulfill order. Please try again.');
    } finally {
      setFulfillingId(null);
    }
  };

  // Filter orders by search query locally
  const filteredOrders = orders.filter((o) => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    return (
      o.memberName.toLowerCase().includes(query) ||
      o.memberPhone.includes(query) ||
      o.productName.toLowerCase().includes(query) ||
      o.redemptionCode.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Title */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#2b2b2b] pb-5">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <ShoppingBag className="text-lime-400" size={28} />
            <span>Member Redemptions Fulfillments</span>
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Fulfill store items purchased by members using their consistency coins.
          </p>
        </div>

        <button 
          onClick={fetchOrders}
          disabled={loading}
          className="p-2 rounded-lg bg-[#222] border border-gray-800 text-gray-400 hover:text-white disabled:opacity-55 transition-colors cursor-pointer self-start sm:self-auto"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-xs flex items-center gap-2">
          <AlertTriangle size={14} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="p-3 bg-[#6CD800]/10 border border-[#6CD800]/20 text-[#6CD800] rounded-lg text-xs flex items-center gap-2">
          <CheckCircle2 size={14} />
          <span>{success}</span>
        </div>
      )}

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-[#181818] p-4 rounded-xl border border-[#2b2b2b]">
        {/* Status Filter Tabs */}
        <div className="flex gap-1.5 bg-[#101010] p-1 rounded-lg border border-gray-800 w-full md:w-auto">
          {['Pending', 'Fulfilled', 'All'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`flex-1 md:flex-initial px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
                statusFilter === status 
                  ? 'bg-[#222] text-[#6CD800]' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {status === 'Pending' ? 'Pending Claim' : status === 'Fulfilled' ? 'Fulfilled' : 'All Orders'}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-2.5 text-gray-500" size={16} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by code, name, or phone..."
            className="w-full bg-[#101010] text-white border border-[#2b2b2b] rounded-lg pl-9 pr-4 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-lime-400"
          />
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 space-y-4">
          <Loader2 className="animate-spin text-[#6CD800]" size={32} />
          <p className="text-gray-400 text-xs">Loading redemption requests...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-16 bg-[#181818] border border-[#2b2b2b] rounded-xl">
          <ShoppingBag className="mx-auto text-gray-700 mb-3" size={32} />
          <p className="text-xs text-gray-400">No orders found matching the criteria.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredOrders.map((o) => {
            const isPending = o.status === 'Pending';
            return (
              <div 
                key={o.id} 
                className={`p-5 rounded-xl border flex flex-col justify-between space-y-4 bg-[#181818] transition-all duration-300 ${
                  isPending ? 'border-[#2b2b2b] hover:border-lime-500/20' : 'border-gray-900 opacity-70'
                }`}
              >
                {/* Product and Cost */}
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <span className="text-[9px] font-bold uppercase tracking-widest text-[#6CD800] bg-[#6CD800]/10 px-2 py-0.5 rounded border border-[#6CD800]/10">
                      Store Reward Item
                    </span>
                    <h4 className="text-base font-extrabold text-white mt-1.5 leading-snug">{o.productName}</h4>
                    <span className="text-xs font-semibold text-gray-400 flex items-center gap-1 mt-1">
                      Cost: <span className="text-[#6CD800]">🪙 {o.coinsSpent} Coins</span>
                    </span>
                  </div>

                  <div className="text-right">
                    {isPending ? (
                      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-1">
                        <Clock size={10} /> Pending
                      </span>
                    ) : (
                      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
                        <CheckCircle2 size={10} /> Fulfilled
                      </span>
                    )}
                    <span className="text-[9px] text-gray-500 block mt-1">
                      {new Date(o.redeemedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Member Details */}
                <div className="bg-[#101010] p-3 rounded-lg border border-gray-850 flex flex-col space-y-1.5 text-xs text-gray-300">
                  <div className="flex items-center gap-2">
                    <User size={12} className="text-gray-500" />
                    <span className="font-bold">{o.memberName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={12} className="text-gray-500" />
                    <span>{o.memberPhone}</span>
                  </div>
                </div>

                {/* Redemption Code / Action */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-850">
                  <div className="flex items-center gap-2">
                    <Ticket size={14} className="text-gray-500" />
                    <span className="text-xs text-gray-400 font-bold">Code:</span>
                    <span className="text-sm font-black text-lime-400 font-mono select-all uppercase">
                      {o.redemptionCode}
                    </span>
                  </div>

                  {isPending ? (
                    <button
                      onClick={() => handleFulfill(o.id)}
                      disabled={fulfillingId === o.id}
                      className="px-3.5 py-1.5 bg-[#6CD800] text-[#0B0F19] hover:bg-[#5bc200] disabled:opacity-50 text-xs font-black uppercase rounded-lg transition-colors flex items-center gap-1"
                    >
                      {fulfillingId === o.id && <Loader2 className="animate-spin" size={12} />}
                      Fulfill Item
                    </button>
                  ) : (
                    o.fulfilledAt && (
                      <span className="text-[10px] text-gray-500 font-semibold">
                        Done {new Date(o.fulfilledAt).toLocaleDateString()}
                      </span>
                    )
                  )}
                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RedemptionOrders;
